import type { ReactNode } from 'react';
import { useLocation } from 'react-router';
import { Loading, Page } from '../../components/ui.tsx';
import { SectionsFrame, type SettingsSection } from '../settings/Frame.tsx';
import { useProject, useProjectId } from './hooks.ts';

/**
 * A project's Settings, in sections, framed as the organization's are (`SectionsFrame`): the project itself, its
 * process, its people, the client's budget (a client's job only), billing and banners (common `SETTINGS_PAGES`, which
 * light Settings in the project's menu). A client reads their budget page as before, with no frame.
 */
export function ProjectSettingsFrame({ children }: { children: ReactNode }) {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const { pathname } = useLocation();
  if (!project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  if (project.viewerRole !== 'contractor') return <>{children}</>;
  const base = `/p/${projectId}`;
  const sections: SettingsSection[] = [
    { label: 'Project', to: `${base}/settings` },
    ...(project.convertedAt !== null ? [{ label: 'Process', to: `${base}/settings/process` }] : []),
    { label: 'People', to: `${base}/people` },
    ...(project.sections.includes('budget') ? [{ label: 'Budget', to: `${base}/budget` }] : []),
    { label: 'Billing', to: `${base}/spent` },
    { label: 'Banners', to: `${base}/banners` },
  ];
  return (
    <main className="shell pb-28 pt-8 sm:pt-12">
      <SectionsFrame label={`${project.name}: settings`} pathname={pathname} sections={sections}>
        {children}
      </SectionsFrame>
    </main>
  );
}
